import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Check,
  ChevronRight,
  X,
  Target,
  Download,
  Bell,
  BookOpen,
  Coins,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DISMISS_KEY = 'sl_onboarding_dismissed_at';
// Only nudge the checklist for the first 30 days of the account. Users past
// that are settled in — surfacing "install the app" feels nagging.
const MAX_ACCOUNT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.navigator?.standalone === true) return true;
  return false;
};

const isInstallDismissedOrAccepted = () => {
  try {
    if (localStorage.getItem('pwa_install_dismissed_permanent') === '1') return true;
  } catch { /* ignore */ }
  return false;
};

/**
 * OnboardingChecklist — first-week guided journey surfaced on the dashboard.
 * Tracks 4 milestones:
 *   1. Take the placement test
 *   2. Install the app (standalone / A2HS accepted)
 *   3. Enable push notifications
 *   4. Start your first book (any /api/progress row)
 *
 * Auto-hides when:
 *   • account older than 30 days
 *   • user explicitly dismisses
 *   • all 4 items are complete (celebration state for 24h, then silent)
 */
const OnboardingChecklist = ({ user, placementTaken }) => {
  const navigate = useNavigate();
  const [bookProgress, setBookProgress] = useState(null); // null = loading
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [dismissedAt, setDismissedAt] = useState(() => {
    try { return parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10); }
    catch { return 0; }
  });
  // Server-tracked claimed milestones. Prevents double-awards after refresh.
  const [claimed, setClaimed] = useState(() => new Set());
  const [rewardMap, setRewardMap] = useState({});
  // A/B copy variants: { [milestone_id]: { variant: 'A'|'B', title, subtitle } }
  // Server assigns deterministically per (user_id, milestone_id) pair.
  const [variants, setVariants] = useState({});
  // Transient toast so the user sees the coins they just earned. Cleared
  // after 3s; purely cosmetic — the backend already persisted the coins.
  const [rewardToast, setRewardToast] = useState(null);

  // Fetch progress + claimed rewards in parallel. Onboarding-claim state
  // lives on the user_gamification doc so the list stays truthful across
  // devices and sessions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [progressRes, onboardRes] = await Promise.all([
          authFetch(`${BACKEND_URL}/api/progress`),
          authFetch(`${BACKEND_URL}/api/gamification/onboarding`),
        ]);
        let prog = [];
        if (progressRes.ok) {
          const d = await progressRes.json();
          prog = Array.isArray(d?.progress) ? d.progress : [];
        }
        let claimedList = [];
        let rewards = {};
        let variantsMap = {};
        if (onboardRes.ok) {
          const d = await onboardRes.json();
          claimedList = Array.isArray(d?.claimed) ? d.claimed : [];
          rewards = d?.rewards || {};
          variantsMap = d?.variants || {};
        }
        if (cancelled) return;
        setBookProgress(prog);
        setClaimed(new Set(claimedList));
        setRewardMap(rewards);
        setVariants(variantsMap);
      } catch {
        if (!cancelled) setBookProgress([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-check notification permission periodically (browsers don't fire an
  // event when the user grants perms from settings — poll cheaply).
  useEffect(() => {
    if (typeof Notification === 'undefined') return undefined;
    const id = setInterval(() => setNotifPerm(Notification.permission), 3000);
    const onVis = () => setNotifPerm(Notification.permission);
    window.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      clearInterval(id);
      window.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, []);

  // Auto-claim reward on server when a milestone first flips to done. The
  // backend is idempotent (addToSet + guarded $inc), so this is safe even if
  // state momentarily disagrees during a render.
  const claimReward = useCallback(async (milestoneId) => {
    if (claimed.has(milestoneId)) return;
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/gamification/claim-onboarding/${milestoneId}`,
        { method: 'POST' }
      );
      if (!r.ok) return;
      const data = await r.json();
      // Optimistically mark locally so we don't refire.
      setClaimed((prev) => {
        const next = new Set(prev);
        next.add(milestoneId);
        return next;
      });
      if (data?.coins_awarded > 0) {
        setRewardToast({ id: milestoneId, coins: data.coins_awarded });
        setTimeout(() => setRewardToast(null), 3200);
      }
    } catch { /* best-effort — user will re-claim on next visit */ }
  }, [claimed]);

  const items = useMemo(() => {
    const installed = isStandalone() || isInstallDismissedOrAccepted();
    const hasStartedBook = Array.isArray(bookProgress) && bookProgress.length > 0;
    const rewardFor = (k) => rewardMap?.[k]?.coins || 0;
    // Helper: prefer server-assigned variant copy, fall back to hardcoded
    // defaults if the user hit this component before /onboarding resolved.
    const copy = (mid, fallbackTitle, fallbackSubtitle) => {
      const v = variants?.[mid];
      return {
        title: v?.title || fallbackTitle,
        subtitle: v?.subtitle || fallbackSubtitle,
      };
    };
    const pl = copy('placement', 'Find your reading level', '~3 minutes');
    const inst = copy('install', 'Install the app', 'Offline reading · lock-screen audio');
    const notif = copy('notifications', 'Enable new-episode alerts', 'Never miss a chapter drop');
    const fb = copy('first-book', 'Start your first book', 'Any ebook, audiobook, or episode counts');
    return [
      {
        key: 'placement',
        title: pl.title,
        subtitle: `+50 coins · ${pl.subtitle}`,
        Icon: Target,
        done: !!placementTaken,
        // Placement mints its own +50 via /api/placement/complete — no claim.
        rewardCoins: 0,
        ringClass: 'from-amber-400 to-orange-500',
        onClick: () => {
          // Scroll up to the existing placement CTA which owns the modal.
          const cta = document.querySelector('[data-testid="home-cta-placement"]');
          if (cta) {
            cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => cta.click(), 400);
          }
        },
      },
      {
        key: 'install',
        title: inst.title,
        subtitle: `+${rewardFor('install')} coins · ${inst.subtitle}`,
        Icon: Download,
        done: installed,
        rewardCoins: rewardFor('install'),
        ringClass: 'from-indigo-400 to-fuchsia-500',
        onClick: () => {
          // Nudge the install banner back into view by clearing snooze and
          // scrolling down. On iOS/unsupported, this at least shows the CTA.
          try {
            localStorage.removeItem('pwa_install_dismissed_at');
          } catch { /* ignore */ }
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        },
      },
      {
        key: 'notifications',
        title: notif.title,
        subtitle: `+${rewardFor('notifications')} coins · ${notif.subtitle}`,
        Icon: Bell,
        done: notifPerm === 'granted',
        rewardCoins: rewardFor('notifications'),
        ringClass: 'from-fuchsia-400 to-pink-500',
        onClick: async () => {
          if (typeof Notification === 'undefined') return;
          if (Notification.permission === 'default') {
            try {
              const res = await Notification.requestPermission();
              setNotifPerm(res);
            } catch { /* ignore */ }
          } else if (Notification.permission === 'denied') {
            // Browser won't re-prompt — send them to profile where we show
            // the "re-enable in browser settings" help.
            navigate('/profile');
          }
        },
      },
      {
        key: 'first-book',
        title: fb.title,
        subtitle: `+${rewardFor('first-book')} coins · ${fb.subtitle}`,
        Icon: BookOpen,
        done: hasStartedBook,
        rewardCoins: rewardFor('first-book'),
        ringClass: 'from-emerald-400 to-teal-500',
        onClick: () => navigate('/library'),
      },
    ];
  }, [placementTaken, bookProgress, notifPerm, navigate, rewardMap, variants]);

  // Auto-claim on transition. We run AFTER rewardMap has loaded (so claims
  // aren't attempted before we know the milestone → coin mapping).
  useEffect(() => {
    if (!rewardMap || Object.keys(rewardMap).length === 0) return;
    items.forEach((it) => {
      if (it.done && it.rewardCoins > 0 && !claimed.has(it.key)) {
        claimReward(it.key);
      }
    });
  }, [items, claimed, claimReward, rewardMap]);

  const totalDone = items.filter((i) => i.done).length;
  const pct = Math.round((totalDone / items.length) * 100);

  // ── Show/hide rules ──────────────────────────────────────────────────────
  // Account age guard: user.created_at is ISO. If we can't parse it, default
  // to showing so the checklist degrades gracefully on first load.
  const accountOld = useMemo(() => {
    if (!user?.created_at) return false;
    const t = new Date(user.created_at).getTime();
    if (!Number.isFinite(t)) return false;
    return Date.now() - t > MAX_ACCOUNT_AGE_MS;
  }, [user?.created_at]);

  // Don't render while progress is loading (prevents flashing "0/4" state).
  if (bookProgress === null) return null;
  if (dismissedAt) return null;
  if (accountOld && totalDone === items.length) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissedAt(Date.now());
  };

  const allDone = totalDone === items.length;

  return (
    <div
      className="mb-6 rounded-2xl overflow-hidden border border-indigo-500/30 bg-gradient-to-br from-slate-800/70 via-indigo-950/60 to-slate-900/70 backdrop-blur-sm"
      data-testid="onboarding-checklist"
    >
      <div className="relative p-4 sm:p-5">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-7 h-7 rounded-full text-gray-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="Hide checklist"
          data-testid="onboarding-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-900/40">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-semibold">
              Your first week
            </p>
            <p className="text-base sm:text-lg font-bold text-white leading-tight">
              {allDone ? "You're all set — happy reading!" : `Unlock the full experience · ${totalDone}/${items.length}`}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="mt-3 h-1.5 w-full rounded-full bg-slate-700/60 overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin="0"
          aria-valuemax="100"
          data-testid="onboarding-progress"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 transition-[width] duration-500"
            style={{ width: `${Math.max(4, pct)}%` }}
          />
        </div>

        {/* Items */}
        <ul className="mt-4 space-y-2">
          {items.map((it) => (
            <li key={it.key}>
              <button
                type="button"
                onClick={it.done ? undefined : it.onClick}
                disabled={it.done}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  it.done
                    ? 'bg-emerald-500/10 border-emerald-500/30 cursor-default'
                    : 'bg-slate-800/60 border-slate-700/60 hover:border-indigo-400/60 hover:bg-slate-800'
                }`}
                data-testid={`onboarding-item-${it.key}`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    it.done
                      ? 'bg-emerald-500 text-white'
                      : `bg-gradient-to-br ${it.ringClass} text-white shadow-md`
                  }`}
                >
                  {it.done ? <Check className="w-5 h-5" /> : <it.Icon className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-semibold ${
                      it.done ? 'text-emerald-200 line-through decoration-emerald-500/60' : 'text-white'
                    }`}
                  >
                    {it.title}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate">{it.subtitle}</p>
                </div>
                {!it.done && (
                  <>
                    {it.rewardCoins > 0 && (
                      <span
                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-bold flex-shrink-0"
                        data-testid={`onboarding-reward-${it.key}`}
                      >
                        <Coins className="w-3 h-3" />+{it.rewardCoins}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </>
                )}
                {it.done && it.rewardCoins > 0 && claimed.has(it.key) && (
                  <span
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold flex-shrink-0"
                    data-testid={`onboarding-claimed-${it.key}`}
                  >
                    <Coins className="w-3 h-3" />+{it.rewardCoins}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {/* Transient reward toast — pops for 3.2s when a milestone claims */}
        {rewardToast && (
          <div
            className="mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-400/40 text-amber-200 text-sm font-semibold animate-in fade-in slide-in-from-bottom-2 duration-300"
            data-testid="onboarding-reward-toast"
          >
            <Coins className="w-4 h-4" />
            +{rewardToast.coins} coins earned!
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
